/**
 * <s-fade-slider> — Stacked cross-fade slider with autoplay and line progress indicators.
 *
 * Every [data-fade-slide] child shares one grid cell; the active one gets [data-active]
 * and the rest are aria-hidden + inert, so CSS handles the fade. Hover does not pause
 * (editor previews keep the cursor over the page); focus inside and the pause control do.
 *
 * Attributes: interval (ms, default 3000), autoplay (presence).
 * Children: [data-fade-dots] (generated buttons), [data-fade-pause], [data-fade-play].
 * Commands: --select (source data-index), --pause, --play.
 */
class SFadeSlider extends HTMLElement {
  #ac;
  #timer = null;
  #index = 0;
  #slides = [];
  #dots = [];
  #focused = false;
  #playing = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.#slides = Array.from(this.querySelectorAll('[data-fade-slide]'));
    const dotsWrap = this.querySelector('[data-fade-dots]');
    const pauseBtn = this.querySelector('[data-fade-pause]');
    const playBtn = this.querySelector('[data-fade-play]');

    if (this.#slides.length < 2) {
      if (dotsWrap) dotsWrap.hidden = true;
      if (pauseBtn) pauseBtn.hidden = true;
      if (playBtn) playBtn.hidden = true;
      this.#show(0);
      return;
    }

    if (dotsWrap) {
      const template = dotsWrap.dataset.labelTemplate || '';
      const total = this.#slides.length;
      this.#dots = this.#slides.map((_, i) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.dataset.index = String(i);
        btn.setAttribute('commandfor', this.id);
        btn.setAttribute('command', '--select');
        btn.setAttribute('aria-label', template.replace('__INDEX__', String(i + 1)).replace('__TOTAL__', String(total)));
        btn.className = 'cursor-pointer relative flex items-center justify-center min-h-touch-target px-1 before:block before:h-0.5 before:w-4 md:before:w-6 before:rounded-full before:bg-on-surface/20 hover:before:bg-on-surface/45 data-active:before:w-8 md:data-active:before:w-12 data-active:before:bg-on-surface motion-safe:before:transition-[width,background-color] motion-safe:before:duration-500 motion-safe:before:ease-ui focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2';
        dotsWrap.appendChild(btn);
        return btn;
      });
    }

    this.addEventListener('command', (event) => {
      if (event.command === '--select') {
        const i = parseInt(event.source?.dataset.index, 10);
        if (!Number.isNaN(i)) {
          this.#show(i);
          this.#restart();
        }
      } else if (event.command === '--pause') {
        this.#setPlaying(false);
      } else if (event.command === '--play') {
        this.#setPlaying(true);
      }
    }, opts);

    this.addEventListener('focusin', () => {
      this.#focused = true;
      this.#stop();
    }, opts);
    this.addEventListener('focusout', (event) => {
      if (this.contains(event.relatedTarget)) return;
      this.#focused = false;
      this.#restart();
    }, opts);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.#stop();
      else this.#restart();
    }, opts);

    this.#show(0);
    this.#setPlaying(this.hasAttribute('autoplay'));
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#stop();
  }

  #show(index) {
    this.#index = (index + this.#slides.length) % this.#slides.length;
    this.#slides.forEach((slide, i) => {
      const active = i === this.#index;
      slide.toggleAttribute('data-active', active);
      slide.toggleAttribute('inert', !active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });
    this.#dots.forEach((dot, i) => {
      const active = i === this.#index;
      dot.toggleAttribute('data-active', active);
      if (active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  #setPlaying(playing) {
    this.#playing = playing;
    const pauseBtn = this.querySelector('[data-fade-pause]');
    const playBtn = this.querySelector('[data-fade-play]');
    if (pauseBtn) pauseBtn.hidden = !playing;
    if (playBtn) playBtn.hidden = playing;
    this.#restart();
  }

  #restart() {
    this.#stop();
    if (!this.#playing || this.#focused || document.hidden) return;
    const interval = parseInt(this.getAttribute('interval'), 10) || 3000;
    this.#timer = setInterval(() => this.#show(this.#index + 1), interval);
  }

  #stop() {
    if (this.#timer) {
      clearInterval(this.#timer);
      this.#timer = null;
    }
  }
}

if (!customElements.get('s-fade-slider')) customElements.define('s-fade-slider', SFadeSlider);
